import React, { useState, useEffect } from 'react';
import SummaryApi from '../common';
import NewsModal from './NewsModal'; // Import NewsModal
const NewsPanel = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentNewsItem, setCurrentNewsItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Manage modal visibility
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [newsPerPage] = useState(8); // Number of news items per page
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch(SummaryApi.adminNews.url, {
          method: SummaryApi.adminNews.method,
        });
        const data = await response.json();
        if (data.status) {
          setNews(data.data);
        } else {
          setError('Failed to load news.');
        }
      } catch (error) {
        setError('An error occurred while fetching the news.');
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);
  const handleCreateNews = () => {
    setCurrentNewsItem(null); // Clear current news item
    setIsModalOpen(true); // Open modal for creating news
  };
  const handleEditClick = (newsItem) => {
    setCurrentNewsItem(newsItem); // Set the item to edit
    setIsModalOpen(true); // Open modal for editing news
  };
  const handleSaveNews = async (newsItem) => {
    try {
      const url = newsItem.news_id
        ? SummaryApi.updateNews.url // Update existing news
        : SummaryApi.createNews.url; // Create new news
      const method = "POST";
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newsItem), // Send updated news with message and is_active as string
      });
      const data = await response.json();
      if (data.status) {
        if (newsItem.news_id) {
          setNews((prevNews) =>
            prevNews.map((item) =>
              item.news_id === newsItem.news_id ? newsItem : item
            )
          );
        } else {
          setNews((prevNews) => [newsItem, ...prevNews]); // Add new news to the list
        }
        setIsModalOpen(false); // Close modal after saving
      } else {
        setError('Failed to save news.');
      }
    } catch (error) {
      setError('An error occurred while saving the news.');
    }
  };
  const handleCancelModal = () => {
    setIsModalOpen(false); // Close modal without saving
  };
  // Pagination logic
  const paginateNews = () => {
    const indexOfLastNews = currentPage * newsPerPage;
    const indexOfFirstNews = indexOfLastNews - newsPerPage;
    return news.slice(indexOfFirstNews, indexOfLastNews);
  };
  const totalPages = Math.ceil(news.length / newsPerPage);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  if (loading) {
    return <div className="text-center text-lg">Loading...</div>;
  }
  if (error) {
    return <div className="text-center text-lg text-red-500">{error}</div>;
  }
  return (
    <div className="p-4">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6 text-center">News Portal</h1>
      {/* Create News Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleCreateNews}
          className="bg-blue-500 text-white p-4 rounded-md hover:bg-blue-600"
        >
          Create News
        </button>
      </div>
      {/* News List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {paginateNews().length > 0 ? (
          paginateNews().map((item) => (
            <div
              className="bg-white border border-gray-200 rounded-lg shadow-md p-4"
              key={item.news_id}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">News {item.news_id}</h3>
                <span className="text-sm text-gray-500">
                  {new Date(item.created_date).toLocaleString()}
                </span>
              </div>
              <div className="text-gray-700">
                <p>{item.message}</p>
              </div>
              {/* Active / Inactive Status Badge */}
              <div className="mt-2">
                <span
                  className={`inline-block text-sm font-semibold px-3 py-1 rounded-md ${
                    item.is_active === "1" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                  }`}
                >
                  {item.is_active === "1" ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => handleEditClick(item)}
                  className="bg-yellow-500 text-white px-4 py-1 rounded-md hover:bg-yellow-600"
                >
                  Edit
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="col-span-full text-center text-lg text-gray-500">No news available</p>
        )}
      </div>
      {/* Pagination Controls */}
      <div className="flex justify-center mt-6">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-1 bg-blue-500 text-white rounded-md disabled:bg-gray-400"
        >
          Prev
        </button>
        <span className="mx-4">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-1 bg-blue-500 text-white rounded-md disabled:bg-gray-400"
        >
          Next
        </button>
      </div>
      {/* News Modal */}
      <NewsModal
        isOpen={isModalOpen}
        onClose={handleCancelModal}
        newsItem={currentNewsItem}
        onSave={handleSaveNews}
      />
    </div>
  );
};
export default NewsPanel;